import React, { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { GUARDS } from "@/lib/guards";

// Lot 671 — Paramètres › Identité › « Garde-fous — stabilité & performance » : un interrupteur par fonctionnalité
// récente coupable potentielle de lourdeur. Effet immédiat sur web + apps (via /config/public), sans mise à jour.
// Lot 742 — liste rangée du lot le plus récent au plus ancien (plus de désordre) + barre de recherche (libellé,
// explication, clé, numéro de lot).
const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
export const sortGuards = (list) => [...list].sort((a, b) => (b.lot || 0) - (a.lot || 0));
export const matchGuard = (d, q) => {
  const n = norm(q).trim();
  if (!n) return true;
  return norm(`${d.label} ${d.key} lot ${d.lot || ""} ${d.hint(true)} ${d.hint(false)}`).includes(n);
};

export const GuardsCard = ({ value, onChange }) => {
  const g = value && typeof value === "object" ? value : {};
  const [q, setQ] = useState("");
  const [onlyOff, setOnlyOff] = useState(false); // lot 743 — puce « Coupés seulement »
  const isOn = (d) => (typeof g[d.key] === "boolean" ? g[d.key] : d.def);
  const set = (key, v) => onChange({ ...g, [key]: v });
  const allOff = () => onChange(Object.fromEntries(GUARDS.filter((d) => d.key !== "flight_recorder").map((d) => [d.key, false])));
  const reset = () => onChange({});
  const sorted = useMemo(() => sortGuards(GUARDS), []);
  const offCount = sorted.filter((d) => !isOn(d)).length;
  const shown = sorted.filter((d) => matchGuard(d, q) && (!onlyOff || !isOn(d)));
  const filtered = !!q || onlyOff;
  // Lot 743 — saut depuis la recherche globale vers une ligne : on lève les filtres pour qu'elle soit visible.
  useEffect(() => {
    const onJump = (e) => { if (String(e.detail?.target || "").startsWith("guard-")) { setQ(""); setOnlyOff(false); } };
    window.addEventListener("yalola:settings-jump", onJump);
    return () => window.removeEventListener("yalola:settings-jump", onJump);
  }, []);
  return (
    <div className="yalola-card p-5 mt-4" data-testid="settings-guards-section">
      <div className="font-bold font-display text-lg mb-1">Garde-fous — stabilité & performance</div>
      <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
        Chaque fonction récente pouvant alourdir l'app ou gêner un membre se coupe ici, <b>immédiatement et sans mise à jour</b> (web, Android et iPhone).
        En cas de lenteur signalée : « Tout couper », observer 10 minutes, puis réactiver un par un pour trouver le coupable.
        La qualité d'encodage vidéo n'est pas concernée. Liste rangée du lot le plus récent au plus ancien.
      </p>
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <button type="button" onClick={allOff} className="text-xs font-semibold rounded-full px-3 py-1.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid="guards-all-off">Tout couper (sauf diagnostic)</button>
        <button type="button" onClick={reset} className="text-xs font-semibold rounded-full px-3 py-1.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid="guards-reset">Valeurs par défaut</button>
        <button
          type="button"
          onClick={() => setOnlyOff((v) => !v)}
          aria-pressed={onlyOff}
          className="text-xs font-semibold rounded-full px-3 py-1.5 border"
          style={onlyOff ? { background: "var(--yalola-primary)", color: "#fff", borderColor: "var(--yalola-primary)" } : { background: "var(--yalola-surface-alt)", borderColor: "transparent" }}
          data-testid="guards-only-off"
          data-state={onlyOff ? "on" : "off"}
        >
          Coupés seulement{offCount ? ` (${offCount})` : ""}
        </button>
        <label className="relative flex items-center ml-auto w-full sm:w-72" data-testid="guards-search">
          <Search size={14} className="absolute left-3 pointer-events-none" style={{ color: "var(--yalola-text-muted)" }} />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un garde-fou (mot, lot…)"
            className="w-full text-sm rounded-full pl-9 pr-8 py-1.5 outline-none focus:ring-2"
            style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text)" }}
            data-testid="guards-search-input"
          />
          {q && (
            <button type="button" onClick={() => setQ("")} className="absolute right-2 p-0.5 rounded-full" aria-label="Effacer" data-testid="guards-search-clear">
              <X size={14} style={{ color: "var(--yalola-text-muted)" }} />
            </button>
          )}
        </label>
      </div>
      <div className="text-[11px] mb-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="guards-count">
        {filtered ? `${shown.length} / ${sorted.length} garde-fous${onlyOff ? " (coupés)" : ""}` : `${sorted.length} garde-fous`}
      </div>
      <div className="space-y-2">
        {shown.map((d) => {
          const on = isOn(d);
          return (
            <div key={d.key} className="flex items-center justify-between gap-3 rounded-xl px-4 py-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`guard-row-${d.key}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium">{d.label}{d.lot ? <span className="ml-1 text-[10px] font-normal" style={{ color: "var(--yalola-text-muted)" }}>lot {d.lot}</span> : null}</div>
                <div className="text-[11px]" style={{ color: on ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }} data-testid={`guard-state-${d.key}`}>{d.hint(on)}</div>
              </div>
              <Switch checked={on} onCheckedChange={(v) => set(d.key, v)} data-testid={`guard-${d.key}`} />
            </div>
          );
        })}
        {!shown.length && (
          <div className="text-sm rounded-xl px-4 py-3" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }} data-testid="guards-empty">
            {onlyOff && !q ? "Aucun garde-fou n'est coupé : tout est actif." : `Aucun garde-fou ne correspond à « ${q} »${onlyOff ? " parmi les coupés" : ""}.`}
          </div>
        )}
      </div>
    </div>
  );
};
